import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink, Mic, Scissors, Utensils, Building2, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/seo/SEOHead';
import { distributor } from '../data/mock';
import { approvedSystems, trustSignals } from '../data/approvedContent';
import productsData from '../data/completeProductsData';
import { useMotion } from '../context/MotionContext';
import { trackCTAClick } from '../utils/analytics';
import GlitchText from '../components/ui/GlitchText';

const iconMap = {
  laryngoStroboscopy: Mic,
  surgery: Scissors,
  swallowingDiagnostics: Utensils,
  officeEndoscopy: Building2,
};

// Real XION imagery for each solution area (replaces generic icon placeholders)
const solutionImageMap = {
  laryngoStroboscopy: productsData.endoSTROBESystem.images[0],
  surgery: productsData.endoSURGERYSpectarSystem.images[0],
  swallowingDiagnostics: productsData.endoPORTABLEVeezarSystem.images[0],
  officeEndoscopy: productsData.endoOFFICEVeezarSystem.images[0],
};

// Solution Section Component
const SolutionSection = ({ solutionKey, solution, index }) => {
  const { prefersReducedMotion } = useMotion();
  const Icon = iconMap[solutionKey] || Building2;
  const solutionImage = solutionImageMap[solutionKey];
  const isEven = index % 2 === 0;

  return (
    <motion.section
      initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`py-16 ${index > 0 ? 'border-t border-white/10' : ''}`}
    >
      <div className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
        {/* Content */}
        <div className={!isEven ? 'lg:order-2' : ''}>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 border border-[#00FFD1]/30 flex items-center justify-center">
              <Icon className="text-[#00FFD1]" size={28} />
            </div>
            <div>
              <p className="terminal-label text-[#00FFD1] text-xs">// Solution Area</p>
              <h2 className="text-3xl font-bold text-white">{solution.name}</h2>
            </div>
          </div>

          <p className="text-white/70 text-lg mb-6 leading-relaxed">
            {solution.description}
          </p>

          {/* Benefits */}
          <ul className="space-y-3 mb-8">
            {solution.benefits.map((benefit, i) => (
              <li key={i} className="flex items-start gap-3 text-white/60">
                <span className="w-1.5 h-1.5 bg-[#00FFD1] rounded-full mt-2 shrink-0" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>

          {/* Named Systems */}
          {solution.namedSystems.length > 0 && (
            <div className="mb-8">
              <p className="terminal-label text-white/50 mb-3">Featured Systems:</p>
              <div className="flex flex-wrap gap-2">
                {solution.namedSystems.map((system, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-[#00FFD1]/10 border border-[#00FFD1]/30 text-[#00FFD1] text-sm font-mono"
                  >
                    {system}
                  </span>
                ))}
              </div>
            </div>
          )}

          <Link to="/contact-ph" className="btn-primary">
            Request Demo (PH)
            <ArrowRight size={16} />
          </Link>
        </div>

        {/* Visual — Real XION imagery */}
        <div className={`${!isEven ? 'lg:order-1' : ''}`}>
          <div className="aspect-video hud-card flex items-center justify-center relative overflow-hidden" data-testid={`solution-visual-${solutionKey}`}>
            {/* Grid + radial glow */}
            <div className="absolute inset-0 grid-bg opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#00FFD1]/10 to-transparent" />

            {solutionImage ? (
              <img
                src={solutionImage}
                alt={solution.name}
                className="relative z-10 max-h-[85%] w-auto object-contain p-6 drop-shadow-[0_0_25px_rgba(0,255,209,0.15)]"
              />
            ) : (
              <Icon className="text-[#00FFD1]/30 relative z-10" size={80} />
            )}

            {/* Terminal label */}
            <div className="absolute bottom-3 left-4 z-20 font-mono text-xs text-white/40 tracking-wider uppercase">{solution.name}</div>

            {/* Corner accents */}
            <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-[#00FFD1]/40" />
            <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-[#00FFD1]/40" />
          </div>
        </div>
      </div>
    </motion.section>
  );
};

// System Card Component
const SystemCard = ({ system }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    data-testid={`system-card-${system.slug}`}
    className="hud-card corner-brackets rounded-lg overflow-hidden"
  >
    <div className="aspect-video bg-gradient-to-br from-[#00FFD1]/10 to-transparent border-b border-white/10 flex items-center justify-center p-6 overflow-hidden">
      {system.images && system.images[0] ? (
        <img 
          src={system.images[0]} 
          alt={system.name}
          className="w-full h-full object-contain"
        />
      ) : (
        <Building2 size={64} className="text-[#00FFD1]/40" />
      )}
    </div>
    <div className="p-6">
      <div className="inline-block px-3 py-1 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full mb-3">
        <span className="text-[#00FFD1] text-xs font-mono">{system.category}</span>
      </div>
      <h3 className="text-2xl font-bold text-white mb-2">{system.name}</h3>
      <p className="text-[#00FFD1] text-sm mb-3">{system.tagline}</p>
      {system.highlight && (
        <div
          data-testid={`system-highlight-${system.slug}`}
          className="flex items-start gap-2 mb-4 px-3 py-2 bg-[#F08A24]/10 border border-[#F08A24]/40 rounded-md"
        >
          <Award size={15} className="text-[#F08A24] mt-0.5 shrink-0" />
          <span className="text-[#F08A24] text-xs font-bold uppercase tracking-wide leading-snug">{system.highlight}</span>
        </div>
      )}
      <p className="text-white/60 text-sm mb-6 line-clamp-3">{system.description}</p>
      <Link to={`/system-solutions/${system.slug}`} data-testid={`view-system-${system.slug}`} className="btn-secondary w-full justify-center">
        View System Details
        <ArrowRight size={16} />
      </Link>
    </div>
  </motion.div>
);

const SystemSolutionsPage = () => {
  const { prefersReducedMotion } = useMotion();

  const handleFMSClick = () => {
    trackCTAClick('fms', 'SystemSolutionsPage');
  };

  return (
    <>
      <SEOHead
        title="System Solutions"
        description="XION Medical integrated endoscopy systems for Philippines healthcare. Laryngo-stroboscopy, surgery, swallowing diagnostics, and office endoscopy solutions."
        path="/system-solutions"
      />
      <Header />
      <main className="pt-32 min-h-screen bg-hud">
        <div className="max-w-7xl mx-auto px-6 py-16">
          {/* Page Header */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <p className="terminal-label text-[#00FFD1] mb-4">// Integrated Solutions</p>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {prefersReducedMotion ? 'System Solutions' : <GlitchText text="System Solutions" />}
            </h1>
            <p className="text-white/60 text-lg max-w-3xl mb-4">
              Complete endoscopy workstations tailored to your clinical environment.
              {' '}{trustSignals.madeInGermany} • {trustSignals.since1991}
            </p>
            <p className="text-white/50 text-sm max-w-2xl">
              {trustSignals.clinicalCollaboration}
            </p>
          </motion.div>

          {/* Featured Complete Systems */}
          {/* System Solutions by Category */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            {/* Flagship: complete XION endoscopy tower */}
            <div
              className="mb-16 hud-card corner-brackets rounded-xl overflow-hidden border border-[#00FFD1]/30"
              data-testid="flagship-endoscopy-tower"
            >
              <div className="grid lg:grid-cols-2 items-stretch">
                <div className="relative bg-gradient-to-br from-[#00FFD1]/10 to-transparent flex items-center justify-center p-8 min-h-[340px]">
                  <div className="absolute inset-0 grid-bg opacity-30" />
                  <img
                    src={productsData.endoscopyTowerSystem.images[0]}
                    alt={productsData.endoscopyTowerSystem.name}
                    className="relative z-10 max-h-[420px] w-auto object-contain drop-shadow-[0_0_30px_rgba(0,255,209,0.18)]"
                  />
                  <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-[#00FFD1]/40" />
                  <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-[#00FFD1]/40" />
                </div>
                <div className="p-8 lg:p-10 flex flex-col justify-center">
                  <div className="inline-block px-3 py-1 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full mb-4 w-fit">
                    <span className="text-[#00FFD1] text-xs font-mono">// Flagship Workstation</span>
                  </div>
                  <h2 className="text-4xl font-bold text-white mb-3">{productsData.endoscopyTowerSystem.name}</h2>
                  <p className="text-[#00FFD1] text-lg mb-4">{productsData.endoscopyTowerSystem.tagline}</p>
                  <p className="text-white/65 mb-8 leading-relaxed">{productsData.endoscopyTowerSystem.description}</p>
                  <Link
                    to="/system-solutions/endoscopy-tower"
                    data-testid="view-system-endoscopy-tower"
                    className="btn-primary w-fit"
                  >
                    Explore the Tower
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>

            {/* Laryngo-stroboscopy and Phoniatrics */}
            <h2 className="text-3xl font-bold text-white mb-8">Laryngo-stroboscopy and Phoniatrics</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {[productsData.endoSTROBESystem, productsData.endoFLEXSpectarSystem, productsData.endoCOMPACTVeezarSystem].map((system, idx) => (
                <SystemCard key={idx} system={system} />
              ))}
            </div>

            {/* Surgery */}
            <h2 className="text-3xl font-bold text-white mb-8">Surgery</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {[productsData.endoSURGERYSpectarSystem, productsData.endoSURGERY3DSpectarSystem].map((system, idx) => (
                <SystemCard key={idx} system={system} />
              ))}
            </div>

            {/* Swallowing Diagnostics */}
            <h2 className="text-3xl font-bold text-white mb-8">Swallowing Diagnostics</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {[productsData.endoFLEXSpectarSystem, productsData.endoCOMPACTVeezarSystem, productsData.endoPORTABLEVeezarSystem].map((system, idx) => (
                <SystemCard key={idx} system={system} />
              ))}
            </div>

            {/* Office Endoscopy */}
            <h2 className="text-3xl font-bold text-white mb-8">Office Endoscopy</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[productsData.endoOFFICESpectarSystem, productsData.endoOFFICEVeezarSystem].map((system, idx) => (
                <SystemCard key={idx} system={system} />
              ))}
            </div>
          </motion.div>

          {/* Solutions */}
          {Object.entries(approvedSystems).map(([key, solution], index) => (
            <SolutionSection
              key={key}
              solutionKey={key}
              solution={solution}
              index={index}
            />
          ))}

          {/* CTA Section */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-[#00FFD1]/10 to-transparent border border-[#00FFD1]/30 p-8 md:p-12 mt-8"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="terminal-label text-[#00FFD1] mb-3">// Philippine Market</p>
                <h3 className="text-2xl font-bold text-white mb-4">
                  Find the Right System for Your Practice
                </h3>
                <p className="text-white/60 mb-6">
                  Our exclusive Philippine distributor can help you evaluate, configure, and implement
                  the ideal XION system solution for your clinical environment.
                </p>
                {/* // Distributor exclusivity statement is client-provided; confirm final legal wording before launch. */}
                <p className="text-sm text-white/40">
                  Exclusive distributor: <span className="text-[#00FFD1]">{distributor.name}</span>
                </p>
              </div>
              <div className="flex flex-col gap-4">
                <Link to="/contact-ph" className="btn-primary w-full justify-center">
                  Request Quotation (PH)
                  <ArrowRight size={16} />
                </Link>
                <a
                  href={distributor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleFMSClick}
                  className="btn-secondary w-full justify-center"
                >
                  Visit FMSInc.
                  <ExternalLink size={16} />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default SystemSolutionsPage;
